import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import {
  Share2, Eye, Copy, Check, Trash2, ExternalLink, Loader2,
  Image as ImageIcon, Video, Music, LayoutGrid,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  image: { icon: ImageIcon, label: 'Image', color: 'text-sky-400 bg-sky-500/10' },
  video: { icon: Video, label: 'Video', color: 'text-purple-400 bg-purple-500/10' },
  audio: { icon: Music, label: 'Audio', color: 'text-emerald-400 bg-emerald-500/10' },
  canvas: { icon: LayoutGrid, label: 'Canvas', color: 'text-orange-400 bg-orange-500/10' },
};

export const SharesPanel = ({ token }) => {
  const [shares, setShares] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchShares = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/shares`, { headers });
      setShares(data);
    } catch {
      setShares([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchShares(); }, [fetchShares]);

  const copyLink = (share) => {
    navigator.clipboard.writeText(`${window.location.origin}${share.share_url}`);
    setCopiedId(share.id);
    toast.success('Public link copied');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const revoke = async (share) => {
    setRevokingId(share.id);
    try {
      await axios.delete(`${API}/shares/${share.id}`, { headers });
      setShares((prev) => prev.filter((s) => s.id !== share.id));
      toast.success('Share link revoked');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    } finally {
      setRevokingId(null);
    }
  };

  if (shares === null) {
    return (
      <div className="py-8 flex justify-center" data-testid="shares-loading">
        <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
      </div>
    );
  }

  if (shares.length === 0) {
    return (
      <div className="py-8 text-center" data-testid="shares-empty">
        <Share2 className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
        <p className="text-sm text-zinc-400">No public share links yet</p>
        <p className="text-xs text-zinc-600 mt-1">Share a creation from your Library or a Canvas board to see it here.</p>
      </div>
    );
  }

  const totalViews = shares.reduce((n, s) => n + (s.views || 0), 0);

  return (
    <div data-testid="shares-panel">
      <div className="flex items-center gap-4 mb-4 text-xs text-zinc-500">
        <span data-testid="shares-count"><span className="text-white font-semibold">{shares.length}</span> active link{shares.length !== 1 ? 's' : ''}</span>
        <span>·</span>
        <span className="flex items-center gap-1" data-testid="shares-total-views">
          <Eye className="w-3.5 h-3.5" />
          <span className="text-white font-semibold">{totalViews}</span> total views
        </span>
      </div>
      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
        {shares.map((share) => {
          const meta = TYPE_META[share.resource_type === 'canvas' ? 'canvas' : (share.media_type || 'image')] || TYPE_META.image;
          const Icon = meta.icon;
          return (
            <div key={share.id} className="flex items-center gap-3 p-3 bg-zinc-800/30 rounded-xl" data-testid={`share-row-${share.id}`}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${meta.color}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-white truncate">{share.title}</p>
                <div className="flex items-center gap-2 text-xs text-zinc-500 mt-0.5">
                  <span>{meta.label}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1" data-testid={`share-views-${share.id}`}>
                    <Eye className="w-3 h-3" />{share.views || 0}
                  </span>
                  <span>·</span>
                  <span>{new Date(share.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => copyLink(share)}
                  className="h-8 w-8 p-0 text-zinc-400 hover:text-white" title="Copy public link"
                  data-testid={`share-copy-${share.id}`}>
                  {copiedId === share.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </Button>
                <a href={`/s/${share.id}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                    title="Open share page" data-testid={`share-open-${share.id}`}>
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
                <Button variant="ghost" size="sm" onClick={() => revoke(share)} disabled={revokingId === share.id}
                  className="h-8 w-8 p-0 text-red-400/70 hover:text-red-400 hover:bg-red-500/10" title="Revoke link"
                  data-testid={`share-revoke-${share.id}`}>
                  {revokingId === share.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
